import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {OhmParseInputResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {IMeshContext} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/contexts/MeshContext';
import {ILinearFormulaToken} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {AnySheet, AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {action} from 'mobx';

export class MeshStateController
{
    @action
    public clearCells(ctx:IMeshContext, range:MeshRange, keepBindings:boolean = true):void
    {
        const {sheetId} = ctx;
        const {spreadsheets: {mesh}, bindings} = commonControllers;
        const meshState = mesh.getState(ctx);

        if( !range || !range.from || !range.to )
            return;

        const {from, to} = range;
        const {size: {numCols, numRows}} = meshState;

        const colFrom:number = from.colIndex != null ? from.colIndex : 0;
        const rowFrom:number = from.rowIndex != null ? from.rowIndex : 0;
        const colTo:number = to.colIndex != null ? to.colIndex : numCols;
        const rowTo:number = to.rowIndex != null ? to.rowIndex : numRows;

        const meshRange:MeshRange = new MeshRange(
            new MeshLocation(colFrom, rowFrom),
            new MeshLocation(colTo, rowTo)
        );

        ctx.sheetCtrl.clearSlots(sheetId, meshRange);

        if( !keepBindings )
            bindings.clearBindingsForRange(sheetId, rowFrom, colFrom, meshRange.rowsCount, meshRange.columnsCount);
    }

    @action
    public clearCell(ctx:IMeshContext, colIndex:number, rowIndex:number, keepBindings:boolean = true):void
    {
        const {sheetId} = ctx;
        const {bindings} = commonControllers;

        if( !sheetId )
            return;

        const slot:AnySlot = ctx.sheetCtrl.findSlot(sheetId, new SlotLocation(colIndex, rowIndex));

        if( slot )
            ctx.sheetCtrl.clearSlot(sheetId, slot.location);

        if( !keepBindings )
            bindings.clearBindingsForRange(sheetId, colIndex, rowIndex, 1, 1);
    }

    @action
    public clearCellValues(ctx:IMeshContext, range:MeshRange):void
    {
        const {mesh} = commonControllers.spreadsheets;
        const {sheetId, sheetCtrl} = ctx;

        const meshState = mesh.getState(ctx);
        const {size: {numCols, numRows}} = meshState;

        MeshUtils.eachCellInRange(range, numRows, numCols, (cellKey:string) =>
        {
            const location:SlotLocation = MeshLocation.fromKey(cellKey).toSlotLocation();

            return sheetCtrl.setSlotValue(sheetId, location, AEmpty.Value);
        });
    }

    @action
    public makeCellsLazy(ctx:IMeshContext, range:MeshRange):void
    {
        const {mesh} = commonControllers.spreadsheets;
        const {sheetId, sheetCtrl} = ctx;

        const meshState = mesh.getState(ctx);
        const {size: {numCols, numRows}} = meshState;

        MeshUtils.eachCellInRange(range, numRows, numCols, (cellKey:string) =>
        {
            const location:SlotLocation = MeshLocation.fromKey(cellKey).toSlotLocation();

            return sheetCtrl.makeSlotLazy(sheetId, location);
        });
    }

    @action
    public makeCellsRegular(ctx:IMeshContext, range:MeshRange):void
    {
        const {mesh} = commonControllers.spreadsheets;
        const {sheetId, sheetCtrl} = ctx;

        const meshState = mesh.getState(ctx);
        const {size: {numCols, numRows}} = meshState;

        MeshUtils.eachCellInRange(range, numRows, numCols, (cellKey:string) =>
        {
            const location:SlotLocation = MeshLocation.fromKey(cellKey).toSlotLocation();

            return sheetCtrl.makeSlotRegular(sheetId, location);
        });
    }

    public getState(ctx:IMeshContext):MeshState
    {
        const {main, workbook} = commonControllers.spreadsheets;
        const {sheetId} = ctx;

        const sheet:AnySheet = main.findAnySheetById(sheetId, true);

        if( !sheet )
        {
            console.warn(`Could not find sheet by id`, sheetId);
            return null;
        }

        if( workbook.isTopLevelSheet(sheet.workbook.id, sheetId) )
        {
            const workbook = main.findWorkbookById(sheet.workbook.id);
            return workbook.view.getMeshStateBySheetId(sheet.id);
        }
        else
        {
            console.warn('WARNING!!!! This case should be called only in WSP and method should be overriden in MeshController');
        }

        return null;
    }

    public convertR1C1toA1(formula:SlotFormula, location:SlotLocation):SlotFormula
    {
        const engine = FormulaEngine.getInstance();
        const ohmResult:OhmParseInputResult = engine.parseInputRC(formula, location);

        const fic = commonControllers.spreadsheets.formulaInput;
        const formulaTokens:ILinearFormulaToken[] = fic.getPlainFormulaTokens(ohmResult.result);
        const formulaTokensText = fic.getFormulaTokensText(formulaTokens, location.colIndex, location.rowIndex, true);

        return formulaTokensText;
    }
}
